import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, Anchor, Eye, EyeOff, ShieldAlert, MailQuestion } from "lucide-react";
import { toast } from "sonner";
import { unlockPassword as apiUnlock, requestPasswordReset } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Unlock() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    setHint(null);
    try {
      await apiUnlock(pw);
      await refresh();
      toast.success("Handshake accepted. Sentinel unlocked.");
      navigate(user?.is_architect ? "/console" : "/welcome");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Unlock failed";
      setHint(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/login");
  };

  const fileResetRequest = async () => {
    if (requesting || requested) return;
    setRequesting(true);
    try {
      const res = await requestPasswordReset("Forgot password — locked out of unlock gate.");
      setRequested(true);
      if (res?.status === "already_pending") {
        toast.info("A reset request is already pending. The Architect will get to it shortly.");
      } else {
        toast.success("Reset request filed. The Architect will deliver a temp password directly.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Request failed";
      toast.error(msg);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#09090b] text-zinc-100 px-6"
      data-testid="unlock-page"
    >
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-emerald-400">
          <Anchor className="h-3 w-3" /> Sentinel · Password Gate
        </div>
        <h1 className="font-heading text-4xl mt-2">Verify to enter.</h1>
        <p className="text-sm text-zinc-500 mt-3">
          Signed in as <span className="text-zinc-300">{user?.email}</span>.
          Type your password to unlock this session.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="unlock-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
              <KeyRound className="h-3 w-3" /> Password
            </label>
            <div className="mt-2 flex gap-2">
              <input
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoFocus
                autoComplete="current-password"
                data-testid="unlock-password-input"
                className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="px-3 border border-zinc-800 text-zinc-400 hover:border-zinc-600"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {hint && (
            <div
              className="flex items-start gap-2 text-[11px] text-rose-400 border border-rose-500/30 bg-rose-500/5 p-3"
              data-testid="unlock-hint"
            >
              <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{hint}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !pw}
            data-testid="unlock-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {busy ? "Verifying…" : "Unlock"}
          </button>

          <button
            type="button"
            onClick={fileResetRequest}
            disabled={requesting || requested}
            data-testid="unlock-request-reset"
            className="w-full inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.3em] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 transition-colors"
          >
            <MailQuestion className={`h-3.5 w-3.5 ${requesting ? "animate-pulse" : ""}`} />
            {requested
              ? "Reset request filed — Architect will reach out"
              : requesting
              ? "Filing…"
              : "Forgot password? Request architect reset"}
          </button>

          <button
            type="button"
            onClick={signOut}
            data-testid="unlock-signout"
            className="w-full text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-zinc-300"
          >
            sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
